import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { Link } from "@/lib/u-router";
import { createSignal, Show, untrack } from "solid-js";
import { fromActorRef, useActorRef } from "@xstate/solid";
import { authRoute } from "./route";
import { api, writeSessionToken } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { primeSessionForAccount, sessionActor } from "@/lib/session-machine";
import { registerAccountFromResponse } from "@/lib/auth";
import { readAccountsStateSync } from "@/lib/accounts";
import { indexOfUserId, rememberLastActiveIndex } from "@/lib/active-account";
import { authFlowSearch } from "@/lib/auth-flow";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import {
  cloneSession,
  createAuthFlowHandlers,
  getAuthFlowMachineLogic,
  isValidSession,
  queryKeys,
  VISITOR_ID,
} from "@mealplan/shared";

import { Button } from "@/components/ui/button";
import { Label, TextField } from "@/components/ui/text-field";
import { AlertCircle, Loader2 } from "lucide-solid";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { msg } from "@lingui/core/macro";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { AnimatedSection } from "@/components/ui/animated-section";
import { MealplanAuthScene } from "@/components/auth/mealplan-auth-scene";
import { SocialAuthButtons } from "@/components/auth/social-auth-buttons";

interface RegisterPageInnerProps {
  db: AbstractPowerSyncDatabase;
}

function RegisterPageInner(props: RegisterPageInnerProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const search = useSearch({ from: registerRoute.id });
  const { connectivityRef, disconnectAndClear, flushGuestUpgrade } = usePowerSyncContext();

  // Multi-account: `?mode=add` registers a NEW account ALONGSIDE the current one
  // (Gmail style) — do NOT pre-sign-out, do NOT merge guest data.
  const isAddingAccount = () => search().mode === "add";

  const [name, setName] = createSignal(import.meta.env.DEV ? "Admin" : "");
  // eslint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n): dev default, not UI
  const [email, setEmail] = createSignal(import.meta.env.DEV ? "admin@email.com" : "");
  // eslint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n): dev default, not UI
  const [password, setPassword] = createSignal(import.meta.env.DEV ? "admin@email.com" : "");

  const actorRef = useActorRef(
    untrack(() =>
      getAuthFlowMachineLogic({
        db: props.db,
        api,
        visitorId: VISITOR_ID,
        persistAuthToken: writeSessionToken,
        isAddingAccount: isAddingAccount(),
        onAccountAuthenticated: async (data) => {
          const account = await registerAccountFromResponse(data);
          if (account) {
            // CRITICAL (Bearer-only): remember the REAL account's index BEFORE
            // the guest-upgrade flush so `getActiveAuthToken` resolves its Bearer
            // immediately. The URL is still `/register`/`/login` and the
            // remembered index still points at the GUEST, so without this the
            // flush's authenticated push would resolve the guest → `null` Bearer
            // → 401 (no cookie fallback under Bearer-only) and the swept rows
            // would never reach the server.
            const index = indexOfUserId(readAccountsStateSync(), account.userId);
            if (index !== null) {
              rememberLastActiveIndex(index);
            }
            if (!isAddingAccount()) {
              await flushGuestUpgrade(account.userId);
            }
            primeSessionForAccount(account);
            if (index !== null) {
              void navigate({ to: "/u/$u", params: { u: index } });
            }
          }
        },
        disconnectAndClear: async () => {
          await disconnectAndClear();
        },
        refetchQueries: async (session) => {
          setName("");
          setEmail("");
          setPassword("");

          if (session) {
            // Proactively sync state to trigger the BootManager redirect immediately
            // without waiting for the background refetch to finish.
            sessionActor.send({
              type: "SERVER_REPLY",
              session: isValidSession(session) ? cloneSession(session) : null,
              error: null,
            });

            queryClient.setQueryData(queryKeys.auth.session(), session);
          }

          await queryClient.refetchQueries();
        },
        isWeb: true,
      }),
    ),
  );
  const state = fromActorRef(actorRef);
  const { send } = actorRef;
  const handlers = createAuthFlowHandlers(send);

  const connectivitySnapshot = fromActorRef(connectivityRef);
  const isServerDown = () => {
    const status = connectivitySnapshot().context.apiStatus;
    return status === "down" || status === "maintenance";
  };

  // The auth-flow machine is FLAT, so `state().value` is a plain string. The
  // real network work happens in `signingUp` (sign-up API + session fetch),
  // reached via the TRANSIENT `authenticating` state (`always`). Treat every
  // non-resting state as loading: only `idle`, `confirmingMerge` (user decision,
  // handled by the dialog) and `success` are not in-flight.
  const isLoading = () => {
    const { value } = state();
    return value !== "idle" && value !== "confirmingMerge" && value !== "success";
  };

  const isFormDisabled = () => isLoading() || isServerDown();
  const error = () => state().context.error;

  const handleSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    handlers.handleSignUp(name(), email(), password());
  };

  return (
    <MealplanAuthScene
      width="wide"
      eyebrow={t(msg`New here`)}
      headline={t(msg`Plan your first week in minutes.`)}
      tagline={t(
        msg`Create an account and your recipes, weekly plan and grocery list are instantly synced and yours — on every device, online or off.`,
      )}
      footer={
        <>
          <p class="text-sm text-muted-foreground">
            {t(msg`Already have an account?`)}{" "}
            <Link
              to="/login"
              search={authFlowSearch(search())}
              class="font-medium text-primary underline-offset-4 hover:underline"
            >
              {t(msg`Sign In`)}
            </Link>
          </p>
          <p class="text-xs text-muted-foreground/60">
            <Link to="/" class="transition-colors hover:text-primary">
              {t(msg`← Back to home`)}
            </Link>
          </p>
        </>
      }
    >
      <AnimatedSection show={isServerDown()}>
        <div class="mb-4 flex items-start gap-3 rounded-xl border border-warning/20 bg-warning/10 p-3">
          <AlertCircle class="mt-0.5 h-5 w-5 shrink-0 text-warning" />
          <p class="text-sm font-medium text-warning">
            {connectivitySnapshot().context.apiStatus === "maintenance"
              ? t(msg`The system is in maintenance. Please try again later.`)
              : t(msg`The server is not responding. Please try again later.`)}
          </p>
        </div>
      </AnimatedSection>

      <form onSubmit={handleSubmit} class="space-y-5">
        <div class="space-y-4">
          <div class="space-y-2">
            <Label for="name">{t(msg`Name`)}</Label>
            <TextField
              id="name"
              type="text"
              placeholder={t(msg`Your name`)}
              autocomplete="name"
              required
              disabled={isFormDisabled()}
              value={name()}
              onInput={(e) => {
                setName(e.currentTarget.value);
              }}
            />
          </div>
          <div class="space-y-2">
            <Label for="email">{t(msg`Email`)}</Label>
            <TextField
              id="email"
              type="email"
              placeholder={t(msg`your@email.com`)}
              autocomplete="email"
              required
              disabled={isFormDisabled()}
              value={email()}
              onInput={(e) => {
                setEmail(e.currentTarget.value);
              }}
            />
          </div>
          <div class="space-y-2">
            <Label for="password">{t(msg`Password`)}</Label>
            <TextField
              id="password"
              type="password"
              placeholder={t(msg`••••••••`)}
              autocomplete="new-password"
              required
              disabled={isFormDisabled()}
              minLength={8}
              value={password()}
              onInput={(e) => {
                setPassword(e.currentTarget.value);
              }}
            />
          </div>
          <AnimatedSection show={Boolean(error())}>
            <p class="text-sm text-destructive">{error()}</p>
          </AnimatedSection>
        </div>

        <div class="flex flex-col gap-3">
          <Button type="submit" class="w-full" loading={isLoading()} disabled={isServerDown()}>
            {isLoading() ? t(msg`Creating account…`) : t(msg`Create Account`)}
          </Button>
          <SocialAuthButtons mode="signup" redirect="/" />
        </div>
      </form>

      <ConfirmDialog
        open={state().value === "confirmingMerge"}
        onOpenChange={(open) => {
          if (!open) {
            handlers.handleCancel();
          }
        }}
        title={t(msg`Keep local data?`)}
        description={t(
          msg`You have unsynced tasks created while offline. Do you want to keep them and merge them with your account, or discard them?`,
        )}
        confirmLabel={t(msg`Keep and merge`)}
        cancelLabel={t(msg`Discard`)}
        onConfirm={handlers.handleConfirmMerge}
        onCancel={handlers.handleConfirmDiscard}
      />
    </MealplanAuthScene>
  );
}

function RegisterPage() {
  const { db, isLoading } = usePowerSyncContext();

  return (
    <Show
      when={!isLoading() && db()}
      fallback={
        <MealplanAuthScene width="wide">
          <div class="flex items-center justify-center py-12">
            <Loader2 class="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </MealplanAuthScene>
      }
    >
      {(database) => <RegisterPageInner db={database()} />}
    </Show>
  );
}

export const registerRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/register",
  validateSearch: (search: Record<string, unknown>): { redirect?: string; mode?: "add" } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    // Multi-account: `?mode=add` registers a second account (Gmail style).
    mode: search.mode === "add" ? "add" : undefined,
  }),
  component: RegisterPage,
});
